/**
 * Enables Paychex applications to create a data pipeline pre-configured
 * to work correctly with Paychex endpoints.
 *
 * ## Importing
 *
 * ### es
 *
 * ```js
 * import { data } from '@paychex/internal';
 * ```
 *
 * ### cjs
 *
 * ```js
 * const { data } = require('@paychex/internal');
 * ```
 *
 * ### amd
 *
 * ```js
 * define(['@paychex/internal'], function({ data }) { ... });
 * require(['@paychex/internal'], function({ data }) { ... });
 * ```
 *
 * ### iife
 *
 * ```js
 * const { data } = window['@paychex/internal'];
 * ```
 *
 * @module data
 */

import { data } from '@paychex/core';

import { withSharedLogic } from './utils/shared';

import {
    withREST,
    withRemote,
    createDiagnostics,
} from './utils/index';

export * as utils from './utils/index';

import type { Tracker } from '@paychex/core/types/trackers';
import type { Adapter, DataLayer, DataProxy, Request, Response, DataDefinition, Diagnostics, Fetch } from '@paychex/core/types/data';

export type { Tracker, Adapter, DataLayer, DataProxy, Request, Response, DataDefinition, Diagnostics, Fetch };

/**
 * Options passed to {@link createDiagnostics} to construct a {@link Diagnostics}
 * function that can be passed to the `@paychex/core` `withDiagnostics` method.
 */
export interface DiagnosticOptions extends Pick<DataLayer, 'fetch' | 'createRequest'> {

    /**
     * The Tracker instance used to fire diagnostic events.
     */
    tracker: Tracker,
}

/**
 * Represents a diagnostic endpoint that should be hit after
 * a network call is aborted when using `rest` or `remote` pipelines.
 */
export interface Endpoint extends DataDefinition {

    /**
     * The name of the endpoint to show in logs.
     */
    name: string

    /**
     * The HTTP status code that indicates a successful request. Used because
     * some endpoints return custom status codes other than 2xx for success.
     *
     * @default 200
     */
    success?: number

}

/**
 * Contains all the data functionality necessary for most Paychex applications.
 * Extends the `@paychex/core` [DataLayer](https://paychex.github.io/core/DataLayer.html).
 *
 * ```js
 * import tracker from '~/path/to/tracker';
 *
 * const pipeline = internal.data.createDataPipeline(tracker, xhr, internal.config.endpoints);
 *
 * export const {
 *   proxy,
 *   setAdapter,
 *   createRequest,
 *   rest,
 *   remote,
 *   fetch,
 * } = pipeline;
 * ```
 */
export interface Pipeline extends DataLayer {

    /**
    * Provides a data pipeline intended for accessing Remote (SiteMinder-
    * authenticated) endpoints within Paychex. Provides the following
    * functionality:
    *
    * - sends cookie credentials
    * - allows for deflate compression
    * - creates performance measures
    * - sends and logs traceability data
    * - runs diagnostics if a call is aborted
    * - sets expected Paychex request headers
    * - wraps payload in a RemoteObjectRequest
    * - throws if Remote messages returned
    *
    * **Response Messages**
    *
    * The data operation will fail with an Error if the response contains
    * messages. The error severity will depend on the types of messages
    * returned:
    *
    * error severity | message severity
    * :--- | :---
    * `FATAL` | `CRITICAL`
    * `ERROR` | `WARNING` or `ERROR`
    * `NONE` | `NOTIFICATION` or `INFORMATION`
    *
    * @param request The Request to send.
    * @returns A Response containing the requested data.
    * @example
    * ```js
    * const getWorkers = {
    *   base: 'company',
    *   path: '/do/json/Workers',
    *   operation: 'getWorkers',
    *   destination: 'paychex.CompanyWorkers',
    * };
    *
    * export async function getWorkersList(companyId) {
    *   const params = { companyId };
    *   const request = createRequest(getWorkers, params);
    *   const response = await pipeline.remote(request)
    *     .catch(errors.rethrow(params));
    *   const workers = models.collection(...response.data);
    *   return models.utils.withOrdering(workers, ['caid', 'lastname', 'firstname']);
    * }
    * ```
    */
    remote: DataLayer['fetch']

    /**
    * Provides a data pipeline intended for accessing {@link https://wiki.paychex.com/display/ENTARCH/ReST+API+Standard REST endpoints}
    * within Paychex. Provides the following functionality:
    *
    * - sends cookie credentials
    * - allows for deflate compression
    * - automatically retries failed GETs
    * - creates performance measures
    * - sends and logs traceability data
    * - runs diagnostics if a call is aborted
    * - sets expected Paychex request headers
    * - unwraps {@link https://wiki.paychex.com/display/ENTARCH/RESTful+Resource+State+Model response messages and data}
    *
    * @param request The Request to send.
    * @returns A Response containing the requested data.
    * @example
    * ```js
    * const getReports = {
    *   base: 'reports',
    *   path: 'reports/:userId'
    * };
    *
    * export async function getReportsList(userId) {
    *   const params = { userId };
    *   const request = createRequest(getReports, params);
    *   const response = await pipeline.rest(request)
    *     .catch(errors.rethrow(params));
    *   const reports = models.collection(...response.data);
    *   return models.utils.withOrdering(reports, ['date'], ['desc']);
    * }
    * ```
    */
    rest: DataLayer['fetch']

    /**
     * Enables run-time configuration of network calls.
     */
    proxy: DataProxy

}

/**
 * Creates a new data pipeline for use in Paychex applications. Encapsulates most of the
 * functionality available within the `@paychex/internal/data/*` modules to make creating
 * data pipelines for Paychex as easy as possible.
 *
 * @param tracker The tracker to use to record various data events.
 * @param adapter The default adapter to use for requests.
 * @param endpoints The diagnostic endpoints to use when a call is aborted.
 * @returns A data pipeline you can use in your Paychex applications.
 * @example
 * ```js
 * import tracker from '~/path/to/tracker';
 *
 * const pipeline = internal.data.createDataPipeline(tracker, xhr, internal.config.endpoints);
 *
 * export const {
 *   proxy,
 *   setAdapter,
 *   createRequest,
 *   rest,
 *   remote,
 *   fetch,
 * } = pipeline;
 * ```
 */
export function createDataPipeline(tracker: Tracker, adapter: Adapter, endpoints: Endpoint[] = []): Pipeline {

    const proxy = data.createProxy();

    const {
        fetch,
        createRequest,
        setAdapter
    } = data.createDataLayer(proxy, adapter);

    const diagnostics = createDiagnostics({
        fetch,
        createRequest,
        tracker,
    }, endpoints);

    const shared = withSharedLogic(fetch, {
        tracker,
        diagnostics,
    });

    const rest = withREST(shared);
    const remote = withRemote(shared);

    return {
        fetch,
        createRequest,
        setAdapter,
        rest,
        remote,
        proxy,
    };

}
